/**
 * Stats Cards Components
 * Version: 6.2.0
 * 
 * Advanced trading statistics and metrics display
 */
import './StatsCards.css'

// ============================================
// Metric Card Component
// ============================================
interface MetricCardProps {
  label: string
  value: string | number
  subValue?: string
  change?: number
  trend?: 'up' | 'down' | 'neutral'
  icon: string
  variant?: 'default' | 'success' | 'warning' | 'danger'
  tooltip?: string
}

function MetricCard({ 
  label, 
  value, 
  subValue, 
  change, 
  trend = 'neutral',
  icon,
  variant = 'default',
  tooltip
}: MetricCardProps) {
  return (
    <div className={`metric-card ${variant}`} title={tooltip}>
      <div className="metric-card-icon">{icon}</div>
      <div className="metric-card-content">
        <span className="metric-card-label">{label}</span>
        <span className="metric-card-value">{value}</span>
        {subValue && <span className="metric-card-sub">{subValue}</span>}
      </div>
      {change !== undefined && (
        <div className={`metric-card-change ${trend}`}>
          {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'}
          <span>{Math.abs(change).toFixed(2)}%</span>
        </div>
      )}
    </div>
  )
}

// ============================================
// Risk Gauge Component
// ============================================
interface RiskGaugeProps {
  value: number
  max?: number
  label?: string
  unit?: string
}

function RiskGauge({ value, max = 100, label, unit = '%' }: RiskGaugeProps) {
  const percentage = Math.min(100, (value / max) * 100)
  const color = percentage < 33 ? 'success' : percentage < 66 ? 'warning' : 'danger'

  return (
    <div className="risk-gauge">
      <div className="gauge-ring">
        <svg viewBox="0 0 100 50">
          <path
            d="M5,50 A45,45 0 0,1 95,50"
            fill="none"
            stroke="var(--bg-glass)"
            strokeWidth="8"
            strokeLinecap="round"
          />
          <path
            d="M5,50 A45,45 0 0,1 95,50"
            fill="none"
            stroke={`var(--${color})`}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={`${percentage * 1.57} 157`}
          />
        </svg>
        <div className="gauge-value">
          <span className="gauge-number">{value.toFixed(1)}</span>
          <span className="gauge-unit">{unit}</span>
        </div>
      </div>
      {label && <span className="gauge-label">{label}</span>}
    </div>
  )
}

// ============================================
// Progress Stat Component
// ============================================
interface ProgressStatProps {
  label: string
  current: number
  target: number
  unit?: string
  showPercentage?: boolean
}

function ProgressStat({ 
  label, 
  current, 
  target, 
  unit = '',
  showPercentage = true 
}: ProgressStatProps) {
  const percentage = Math.min(100, (current / target) * 100)
  const isComplete = current >= target

  return (
    <div className="progress-stat">
      <div className="progress-header">
        <span className="progress-label">{label}</span>
        <span className="progress-value">
          {current.toLocaleString()}{unit}
          {showPercentage && ` / ${target.toLocaleString()}${unit}`}
        </span>
      </div>
      <div className="progress-bar-container">
        <div 
          className={`progress-bar ${isComplete ? 'complete' : ''}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {showPercentage && (
        <span className="progress-percentage">{percentage.toFixed(1)}%</span>
      )}
    </div>
  )
}

// ============================================
// Stat Row Component
// ============================================
interface StatRowProps {
  label: string
  value: string | number
  subLabel?: string
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'highlight'
}

function StatRow({ label, value, subLabel, variant = 'default' }: StatRowProps) {
  return (
    <div className={`stat-row ${variant}`}>
      <div className="stat-row-content">
        <span className="stat-row-label">{label}</span>
        {subLabel && <span className="stat-row-sub">{subLabel}</span>}
      </div>
      <span className="stat-row-value">{value}</span>
    </div>
  )
}

// ============================================
// Mini Sparkline Component
// ============================================
interface SparklineProps {
  data: number[]
  color?: string
  width?: number
  height?: number
}

function Sparkline({ 
  data = [], 
  color = 'var(--primary)',
  width = 80,
  height = 30
}: SparklineProps) {
  if (data.length < 2) return null

  const min = Math.min(...data)
  const max = Math.max(...data)
  const range = max - min || 1

  const points = data.map((value, i) => {
    const x = (i / (data.length - 1)) * width
    const y = height - ((value - min) / range) * height
    return `${x},${y}`
  }).join(' ')

  return (
    <svg className="sparkline" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// ============================================
// Ratio Display Component
// ============================================
interface RatioDisplayProps {
  label: string
  wins: number
  losses: number
  showBar?: boolean
}

function RatioDisplay({ label, wins, losses, showBar = true }: RatioDisplayProps) {
  const total = wins + losses
  const winRate = total > 0 ? (wins / total) * 100 : 0
  const lossRate = 100 - winRate

  return (
    <div className="ratio-display">
      <div className="ratio-header">
        <span className="ratio-label">{label}</span>
        <span className="ratio-value">{winRate.toFixed(1)}%</span>
      </div>
      {showBar && (
        <div className="ratio-bar">
          <div 
            className="ratio-bar-wins"
            style={{ width: `${winRate}%` }}
          />
          <div 
            className="ratio-bar-losses"
            style={{ width: `${lossRate}%` }}
          />
        </div>
      )}
      <div className="ratio-details">
        <span className="ratio-wins">{wins} wins</span>
        <span className="ratio-losses">{losses} losses</span>
      </div>
    </div>
  )
}

// ============================================
// Export
// ============================================
export { 
  MetricCard,
  RiskGauge,
  ProgressStat,
  StatRow,
  Sparkline,
  RatioDisplay
}

export type { 
  MetricCardProps,
  RiskGaugeProps,
  ProgressStatProps,
  StatRowProps,
  SparklineProps,
  RatioDisplayProps
}